// @ts-check
"use strict"

import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import { getFileExtension } from "@minohealth/common/utils/files";
import { getSpacesPresignedUrl } from "@minohealth/common/services/SpacesService";
import AnokyeApiService from "@minohealth/common/services/AnokyeApiService";
import { useSelector } from "react-redux";
import { CanceledError } from "axios";

import { IoMdClose } from "react-icons/io";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { MdOutlineDescription, MdOutlineAssignment, MdOutlineChevronRight, MdOutlineChevronLeft } from "react-icons/md";
// We'll handle the report display without direct dependencies on these components
// to avoid TypeScript errors

import "./dicom-image-viewer.css";

/**
 * Generic image viewer component that works directly with a ChatImage object
 * 
 * @typedef {Object} ImageViewerProps
 * @property {DicomViewerImage} image - The image object to display
 * @property {() => void} [onCloseViewer] - Callback when viewer is closed
 */
const DicomImageViewer = forwardRef(
  /**
   * @param {ImageViewerProps & React.RefAttributes<HTMLDialogElement>} props
   * @param {React.ForwardedRef<HTMLDialogElement>} ref
   */
  ({ image, onCloseViewer }, ref) => {

  const dialogRef = useRef(null)
  useImperativeHandle(ref, () => dialogRef.current)
  const { dicomViewerUrl, fallbackUrl } = useViewerImage(image)
  const [showReport, setShowReport] = useState(false)
  
  const toggleReport = () => {
    setShowReport(prev => !prev)
  }

  return (
    <dialog
      className="dicom-image-viewer"
      ref={dialogRef}
      onClick={(e) => {
        if (e.target !== dialogRef.current) return;
        onCloseViewer?.();
      }}
    >
      <div className={`dicom-image-viewer__body ${showReport ? 'with-report' : ''}`}>
        <button
          className="dicom-image-viewer__close-btn"
          onClick={onCloseViewer}
        >
          <IoMdClose />
        </button>
        
        <button
          className={`dicom-image-viewer__report-toggle-btn ${showReport ? 'active' : ''}`}
          onClick={toggleReport}
          title={showReport ? "Hide Report" : "Show Report"}
        >
          {showReport ? <MdOutlineChevronRight /> : <MdOutlineAssignment />}
          <span>{showReport ? "Hide Report" : "View Report"}</span>
        </button>
        
        <div className="dicom-image-viewer__content-wrapper">
          <div className="dicom-image-viewer__img-wrapper" data-loading={dicomViewerUrl == null}>
            {(dicomViewerUrl == null) && (
              <>
                <img 
                  className="dicom-image-viewer__img"
                  src={fallbackUrl} 
                  alt="image" 
                />
                <div className="dicom-image-viewer__loading-indicator">
                  <AiOutlineLoading3Quarters
                    className="spin"
                    // @ts-ignore
                    style={{ "--duration": "1s" }}
                  />
                </div>
              </>
            )}
            {dicomViewerUrl != null && (
              <iframe
                className="dicom-image-viewer__frame"
                src={dicomViewerUrl}
              />
            )}
          </div>
          
          {showReport && (
            <div className="dicom-image-viewer__report-wrapper">
              <DicomReportOverview />
            </div>
          )}
        </div>
      </div>
    </dialog>
  );
});

/** @param {DicomViewerImage} image */
export function useViewerImage(image) {
  const [dicomViewerUrl, setDicomViewerUrl] = useState(null)
  /** @type {ReactState<string | null>} */
  const [localFallbackUrl, setLocalFallbackUrl] = useState(null)
  /** @type {ReactState<string | null>} */
  const [remoteFallbackUrl, setRemoteFallbackUrl] = useState(null)
  // @ts-ignore - We know the structure of our Redux state
  const user = useSelector(state => state?.app?.datastate?.user) || {};

  useEffect(() => {
    if(user.userToken == null) return;
    if(image.dicomUrls.length == 0) return;
    if(image.studyInstanceUid == null) return;

    const controller = new AbortController();
    loadDicomViewerUrl(image.dicomUrls, image.studyInstanceUid, user.userToken, { signal: controller.signal })
      .then(dicomViewerUrl => {
        setDicomViewerUrl(dicomViewerUrl)
      })
      .catch(err => {
        if(err instanceof CanceledError) return;
        console.error(err)
      })

    return () => controller.abort()
  }, [user.userToken, image.dicomUrls, image.studyInstanceUid])

  // Remote fallback: get presigned URL directly
  useEffect(() => {
    if(dicomViewerUrl != null) return;
    if(image.fallback?.localFile != null) return;
    if(image.fallback?.url == null) return;

    let cancelled = false;
    getSpacesPresignedUrl({ objectUrl: image.fallback.url, userToken: user.userToken })
      .then(presignedUrl => {
        if (!cancelled) setRemoteFallbackUrl(presignedUrl)
      })
      .catch(err => console.error(err))

    return () => { cancelled = true }
  }, [image.fallback?.url, dicomViewerUrl, user.userToken])

  // Local fallback: create blob URL from File object
  useEffect(() => {
    const localFile = image.fallback?.localFile
    if(localFile == null) return;

    const previewUrl = URL.createObjectURL(localFile)
    setLocalFallbackUrl(previewUrl)
    return () => {
      setLocalFallbackUrl(null)
      URL.revokeObjectURL(previewUrl)
    }
  }, [image.fallback?.localFile])

  const fallbackUrl = localFallbackUrl ?? remoteFallbackUrl ?? DEFAULT_IMAGE_PREVIEW_SRC

  return { dicomViewerUrl, fallbackUrl }
}

/**
 * @param {string[]} urls
 * @param {string} studyInstanceUid
 * @param {string} userToken
 * @param {Object} [options]
 * @param {AbortSignal} [options.signal]
 */
async function loadDicomViewerUrl(urls, studyInstanceUid, userToken, { signal } = {}) {
  try {
    const { status, data } = await AnokyeApiService(userToken).PostPacsDicomViewUrl({
      fileUrl: urls,
      studyInstanceUid,
    }, { signal })

    if (!status.toString().match(/^2.*/)) {
      throw new Error("Failed to get dicom viewer url")
    }
    return data.viewerUrl
  } catch (err) {
    if(err instanceof CanceledError) throw err
    
    Sentry.captureException(err, {
      extra: {
        context: 'DicomImageViewer - loadDicomViewerUrl',
        studyInstanceUid: studyInstanceUid || 'undefined',
        urlsCount: urls?.length || 0,
        userToken: userToken ? 'present' : 'missing',
        isCanceled: err instanceof CanceledError
      }
    });
    
    throw new Error("Failed to get dicom viewer url")
  }
}


const DEFAULT_IMAGE_PREVIEW_SRC = "https://minohealth-storage.fra1.cdn.digitaloceanspaces.com/chat_placeholder_img.jpg";

/**
 * A simplified version of ReportOverview for use within the DICOM viewer
 * This component provides a basic report display without dependencies on external components
 */
function DicomReportOverview() {
  // Type-safe state access
  const appState = useSelector((state) => state);
  const user = appState?.app?.datastate?.user || {};
  const userToken = user.userToken;
  
  const [reportData, setReportData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Get report ID from URL or context
  const pathSegments = window.location.pathname.split("/");
  const reportId = pathSegments[pathSegments.length - 1];

  
  useEffect(() => {
    if (!userToken || !reportId) return;
    
    async function fetchReport() {
      try {
        setIsLoading(true);
        const response = await AnokyeApiService(userToken).GetResources({
          resourceType: "reportById",
          reportId: reportId,
        });

        if (response?.data) {
          setReportData(response.data);
        }
      } catch (err) {
        Sentry.captureException(err, {
          extra: {
            context: 'DicomImageViewer - DicomReportOverview fetchReport',
            reportId: reportId || 'undefined',
            resourceType: 'reportById',
            userToken: userToken ? 'present' : 'missing',
            userId: appState?.app?.datastate?.user?._id || 'undefined'
          }
        });
        console.error("Failed to get report data", err);
      } finally {
        setIsLoading(false);
      }
    }

    fetchReport();
  }, [userToken, reportId]);

  // If still loading, show loading state
  if (isLoading) {
    return (
      <div className="report-overview-container">
        <div className="report-overview-header">
          <h2 className="report-overview-title">Report Overview</h2>
        </div>
        <div className="report-overview-main">
          <div className="report-loading" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <AiOutlineLoading3Quarters className="spin" style={{ fontSize: '2rem', animation: 'spin 1s linear infinite' }} />
            <p>Loading report...</p>
          </div>
        </div>
      </div>
    );
  }

  // If no report data was found
  if (!reportData) {
    return (
      <div className="report-overview-container">
        <div className="report-overview-header">
          <h2 className="report-overview-title">Report Overview</h2>
        </div>
        <div className="report-overview-main">
          <div style={{ padding: '1rem', textAlign: 'center' }}>
            <p>No report data available</p>
          </div>
        </div>
      </div>
    );
  }

  // Format date if available
  const formattedDate = reportData.createdAt 
    ? new Date(reportData.createdAt).toLocaleDateString() 
    : 'Date not available';

  return (
    <div className="report-overview-container">
      <div className="report-overview-header">
        <div className="report-overview-header-content">
          <div className="report-overview-header-info">
            <h2 className="report-overview-title">Report Overview</h2>
            <p className="report-overview-date">{formattedDate}</p>
          </div>
        </div>
      </div>
      <div className="report-overview-main">
        <div className="report-overview-content">
          <div className="report-overview-viewer">
            <div className="report-actions">
              <button className="report-action-btn">
                <MdOutlineDescription />
                <span>View Report</span>
              </button>
            </div>
            <div className="report-preview-container">
              <div className="report-preview-pdf">
                <div style={{ padding: '1rem' }}>
                  {/* Display report fields in a structured way */}
                  {Object.entries(reportData).map(([key, value]) => {
                    // Skip complex objects or empty values
                    if (typeof value === 'object' || !value) return null;
                    
                    // Format key for display
                    const formattedKey = key
                      .replace(/([A-Z])/g, ' $1')
                      .replace(/^./, str => str.toUpperCase());
                    
                    return (
                      <div key={key} style={{ marginBottom: '1rem' }}>
                        <h3 style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>{formattedKey}</h3>
                        <p style={{ marginTop: 0 }}>{value}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DicomImageViewer;

/**
 * @typedef {Object} DicomViewerImage
 * @property {string} studyInstanceUid
 * @property {string[]} dicomUrls
 * @property {Object} [fallback]
 * @property {string} [fallback.url]
 * @property {File} [fallback.localFile]
 */